import React, { useState, useEffect, useMemo } from "react";
import { useGetProductsQuery } from "../../../../product/api/productApi";
import { useGetTagsQuery } from "../../../../product/api/productTaxonomyApi";
import FeaturedProducts from "./FeaturedProducts";
import FeaturedProductsSkeleton from "./FeaturedProductsSkeleton";
import RTKQErrorCard from "../../../../../common/components/RTKQErrorCard";

interface FeaturedProductsContainerProps {
  rootId: string;
}

const FeaturedProductsContainer: React.FC<FeaturedProductsContainerProps> = ({
  rootId,
}) => {
  const [title, setTitle] = useState("");
  const [seeAllLink, setSeeAllLink] = useState("");
  const [seeAllText, setSeeAllText] = useState("");
  const [tagSlug, setTagSlug] = useState<string | null>(null);
  // --- START MODIFICATION ---
  const [paginationClass, setPaginationClass] = useState<string>("");
  // --- END MODIFICATION ---

  // --- Get ACF data from the root element ---
  useEffect(() => {
    const rootElement = document.getElementById(rootId);
    if (rootElement) {
      setTitle(rootElement.dataset.title || "");
      setSeeAllLink(rootElement.dataset.seeAllLink || "");
      setSeeAllText(rootElement.dataset.seeAllText || "");
      setTagSlug(rootElement.dataset.tagSlug || null);
      // --- START MODIFICATION ---
      setPaginationClass(rootElement.dataset.paginationClass || "");
      // --- END MODIFICATION ---
    }
  }, [rootId]);

  // --- Data Fetching ---
  const {
    data: tagsData,
    isLoading: isLoadingTags,
    isError: isTagsError,
    error: tagsError,
  } = useGetTagsQuery(undefined, {
    skip: !tagSlug,
  });

  const featuredTagId = useMemo(() => {
    if (!tagSlug || !tagsData?.tags) return null;
    const tag = Object.values(tagsData.tags).find((t) => t.slug === tagSlug);
    return tag ? tag.id : null;
  }, [tagsData, tagSlug]);

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    isError: isProductsError,
    error: productsError,
  } = useGetProductsQuery(
    { tag: featuredTagId ? [featuredTagId] : [], limit: 8 },
    { skip: !tagSlug || isLoadingTags || featuredTagId === null }
  );

  const isLoading =
    !tagSlug || isLoadingTags || (featuredTagId !== null && isLoadingProducts);
  const isError = isTagsError || isProductsError;
  const displayError = tagsError || productsError;

  if (isLoading) {
    return <FeaturedProductsSkeleton title={title} />;
  }

  if (isError) {
    return (
      <RTKQErrorCard
        error={displayError as any}
        title="Error loading featured products"
      />
    );
  }

  if (!productsData || productsData.products.length === 0) {
    return null;
  }

  return (
    <FeaturedProducts
      products={productsData.products}
      title={title}
      seeAllLink={seeAllLink}
      seeAllText={seeAllText}
      // --- START MODIFICATION ---
      paginationClassName={paginationClass} // Pass the unique class name
      // --- END MODIFICATION ---
    />
  );
};

export default FeaturedProductsContainer;
